/**
 * Scoring utilities and display constants for Radar dashboard.
 */

import type { LucideIcon } from 'lucide-react'

/**
 * Grade colors for TEXT, borders and bare swatches.
 *
 * Reads the `--pm-grade-*` tokens rather than restating hex values, because
 * those tokens are theme-aware and this map was not. globals.css gained a
 * light-theme grade set when the dashboard got its light variant; this map
 * never did, so on any light surface Grade A rendered as the dark-surface
 * green `#22c55e` — 2.28:1 on white, which is the F-05 score failure at
 * 27px in the signal band. Pointing at the token means a grade colour
 * cannot be theme-wrong again, rather than one value being corrected.
 *
 * Consequence to respect: these are now `var(...)` strings, so they can NOT
 * be concatenated with a hex alpha suffix (`${GRADE_COLORS[g]}15`). Use
 * `color-mix(in srgb, ${GRADE_COLORS[g]} 8%, transparent)` for a tint — the
 * pattern the tool result panels already use for `--warning-text`.
 */
export const GRADE_COLORS: Record<string, string> = {
  A: 'var(--pm-grade-a)',
  B: 'var(--pm-grade-b)',
  C: 'var(--pm-grade-c)',
  D: 'var(--pm-grade-d)',
  F: 'var(--pm-grade-f)',
}

// Radar's A-F grade sequence as a clipped text gradient. It lives with the
// scoring display constants so non-homepage consumers do not import the hero.
export const SCORE_TEXT_GRADIENT = [
  'linear-gradient(90deg,',
  `${GRADE_COLORS.A} 0%,`,
  `${GRADE_COLORS.B} 26%,`,
  `${GRADE_COLORS.C} 52%,`,
  `${GRADE_COLORS.D} 74%,`,
  `${GRADE_COLORS.F} 100%`,
  ')',
].join(' ')

/**
 * Grade colors darkened to carry a white label at WCAG AA (>= 4.5:1).
 *
 * `GRADE_COLORS` are display colors, tuned to read as text and hairlines
 * against the page background. They are NOT safe as a filled background under
 * white type: white on the Grade D orange `#f97316` is only 2.80:1, and on the
 * Grade A green `#22c55e` only 2.28:1.
 *
 * Same split the design system already makes for `--cta` / `--cta-fill` and
 * `--primary` / `--primary-fill` (globals.css). Hue and grade semantics are
 * unchanged; only the luminance moves. Use these ONLY for a filled chip with a
 * white label, and keep `GRADE_COLORS` for text, borders, and bare swatches.
 *
 * B and F reuse the existing calibrated fill tokens so there is one value per
 * brand color, not two.
 */
export const GRADE_FILL_COLORS: Record<string, string> = {
  A: '#15803d', // 5.01:1 with white
  B: 'var(--primary-fill)', // #0077b3, 4.90:1
  C: '#a16207', // 4.92:1
  D: '#c2410c', // 5.18:1
  F: 'var(--cta-fill)', // #d9097a, 4.92:1
}

export const CATEGORY_LABELS: Record<
  string,
  { label: string; color: string; icon: LucideIcon }
> = {} // Populated at runtime from page.tsx (icons are React components, can't be serialized)

export const EFFORT_LABELS: Record<string, { label: string; color: string }> = {
  low: { label: 'Quick Win', color: '#22c55e' },
  medium: { label: 'Moderate', color: 'var(--warning-text)' },
  high: { label: 'Major Effort', color: '#F90B8A' },
}

// IMPACT_LABELS was removed in T4. It rendered "High Impact" from
// ActionItem.impact, a hand-authored severity string with no measurement
// behind it, on the actions panel, the action plan and the HTML export.
// Effort is still labelled (EFFORT_LABELS) because it states an
// implementation cost rather than claiming an outcome. Do not reintroduce
// an impact label without measured inputs behind it.

/**
 * The ONE authoritative grade-band configuration. Each band carries its
 * letter, its half-open score interval, and the theme-scoped display color.
 * `min` is the inclusive floor; `max` is the EXCLUSIVE upper bound and
 * equals the next band's floor, so a boundary score (30/50/70/85) belongs
 * to the band whose `min` it is — the intervals can never overlap. The one
 * exception is the last band: A's max of 100 is inclusive, because 100 is a
 * real score. Ordered lowest band first — the same left-to-right order the
 * GradeScale bar renders (0/F at the left, 100/A at the right).
 *
 * Everything grade-related derives from this table: `gradeFromScore`, the
 * bar's proportional color segments, letter positions, marker placement, and
 * accessibility text. Never define a second grading model or hand-place any
 * of that geometry. The thresholds (30/50/70/85) are the product-wide bands
 * every Radar surface has always graded with — unchanged here.
 */
export const GRADE_BANDS = [
  { grade: 'F', min: 0, max: 30, color: 'var(--pm-grade-f)' },
  { grade: 'D', min: 30, max: 50, color: 'var(--pm-grade-d)' },
  { grade: 'C', min: 50, max: 70, color: 'var(--pm-grade-c)' },
  { grade: 'B', min: 70, max: 85, color: 'var(--pm-grade-b)' },
  { grade: 'A', min: 85, max: 100, color: 'var(--pm-grade-a)' },
] as const

export function gradeFromScore(s: number): string {
  for (let i = GRADE_BANDS.length - 1; i >= 0; i--) {
    if (s >= GRADE_BANDS[i].min) return GRADE_BANDS[i].grade
  }
  return GRADE_BANDS[0].grade
}

export function elapsed(start: number, end: number | null): string {
  const ms = (end || Date.now()) - start
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`
}

/**
 * Tool groupings for the "LLM Infrastructure Audit" vs "AI Citation Monitoring" sections.
 * This distinction is central to Radar's positioning as a *technical readiness* tool,
 * not just another monitoring dashboard.
 */
export const TOOL_GROUPS = {
  infrastructure: {
    label: 'LLM Infrastructure Audit',
    keys: [
      'crawl',
      'robots',
      'llms',
      'readiness',
      'schemaAudit',
      'aeo',
    ] as const,
    color: '#008dcc',
  },
  monitoring: {
    label: 'AI Citation Monitoring',
    keys: [
      'citation',
      'reddit',
      'answer',
      'sourceInfluence',
      'promptSov',
      'hallucinationCheck',
      'brandDisambiguation',
    ] as const,
    color: '#F90B8A',
  },
} as const

/**
 * Crawl Integrity Score: composite metric across all bot access / crawlability checks.
 * Uses crawl (13 bot tests), robots (16 bot rules), and llms.txt validation.
 */
export function crawlIntegrityScore(
  tools: Record<
    string,
    { status: string; data: { overallScore?: number } | null }
  >
): {
  score: number
  grade: string
  components: { key: string; score: number }[]
} {
  const crawlKeys = ['crawl', 'robots', 'llms'] as const
  const components: { key: string; score: number }[] = []
  for (const key of crawlKeys) {
    const t = tools[key]
    if (t?.status === 'done' && t.data?.overallScore !== undefined) {
      components.push({ key, score: t.data.overallScore })
    }
  }
  if (components.length === 0) return { score: 0, grade: 'F', components: [] }
  const avg = Math.round(
    components.reduce((sum, c) => sum + c.score, 0) / components.length
  )
  return { score: avg, grade: gradeFromScore(avg), components }
}
